import { Anchor } from "@/app/components/anchor"
import ResponsiveIframeEmbed from "@/components/tools/ResponsiveIframeEmbed"
import CostPerMileTrendChart from "@/components/cost/CostPerMileTrendChart"
import {
  describeTrend,
  formatSnapshotMonth,
  hasRenderableTrend,
} from "@/util/describe-trend"
import type { CostSummaryTrend } from "@/util/cost-summary"

type CalculatorEmbed = {
  iframeUrl: string
  title: string
  responsiveHeight?: {
    xs?: string
    sm?: string
    md?: string
    lg?: string
    xl?: string
  }
}

type DogBreedCostSectionProps = {
  breed: string
  calculator: CalculatorEmbed
  // tools.json id, used only for the <noscript> link (keeps tools.json out of
  // the client bundle — see .claude/rules/programmatic-pages.md).
  calculatorToolId: string
  // Marketplace-wide dog trend from the committed snapshot. The chart is the same
  // on every breed page; there is no breed-level cost data, so the chart copy
  // stays breed-neutral and names its real (marketplace) scope. Null hides the
  // chart block. See docs/adr/0004-dog-breed-cost-section.md.
  marketplaceTrend: CostSummaryTrend | null
  costDataWindow: { from: string; to: string } | null
}

// Dog breed cost section: the /dogs calculator on top, the marketplace-wide
// cost-per-mile trend chart below. Unlike the pet/boat cost sections there is no
// band (breed pages have no location and the feed has no breed dimension), and
// only the calculator copy references the breed — the chart is marketplace-wide
// and labelled as such, so its identical-across-breeds numbers are never read as
// breed-specific.
export default function DogBreedCostSection({
  breed,
  calculator,
  calculatorToolId,
  marketplaceTrend,
  costDataWindow,
}: DogBreedCostSectionProps) {
  const hasTrend = hasRenderableTrend(marketplaceTrend)

  return (
    <section className="mt-10 md:mt-20" id="shipping-cost">
      <div className="max-w-[1289px] mx-auto px-4 md:px-8 mb-12">
        <div>
          <h2 className="mb-3 text-center font-semibold font-montserrat text-[32px] leading-10 text-blue7">
            What will it cost to ship your {breed}?
          </h2>
          <p className="max-w-3xl mx-auto mb-5 text-center text-md text-gray-600">
            Tell us where your {breed} is starting from and where they're
            headed, and we'll work out a ballpark for the trip. It runs on real
            CitizenShipper booking data, it's free, and there's no account to
            set up.
          </p>
          {/* Widen on mobile to match the boat/pet embeds: this section sits
              inside the page's px-4 wrapper, so cancel one px-4 layer on just the
              embed and chart. */}
          <div className="-mx-4 md:mx-0">
            <ResponsiveIframeEmbed
              src={calculator.iframeUrl}
              title={calculator.title}
              defaultHeight="770px"
              responsiveHeight={calculator.responsiveHeight}
              maxWidth="1000px"
              // The calculator reports its height on mount; its real minimum is
              // the form. Floor well under that so the tiny pre-render post can't
              // squish the frame below the responsive fallback before it lays out.
              minDynamicHeight={300}
            />
          </div>
          <noscript>
            <p className="max-w-3xl mx-auto mt-4 text-center text-sm text-gray-600">
              The cost calculator needs JavaScript to run. You can open the full{" "}
              <Anchor
                href={`/tools/${calculatorToolId}`}
                className="text-blue7 underline"
              >
                Dog Shipping Cost Calculator
              </Anchor>{" "}
              to estimate your cost.
            </p>
          </noscript>
        </div>

        {hasTrend &&
          marketplaceTrend &&
          (() => {
            const trendSummary = describeTrend(marketplaceTrend)
            return (
              <div className="mt-12 max-w-3xl mx-auto">
                <h3 className="mb-2 text-center font-semibold font-montserrat text-xl md:text-2xl">
                  How dog shipping prices have moved lately
                </h3>
                <p className="mb-4 text-center text-md text-gray-600">
                  These numbers come from dog shipments booked across the
                  CitizenShipper marketplace
                  {costDataWindow
                    ? ` between ${formatSnapshotMonth(
                        costDataWindow.from,
                      )} and ${formatSnapshotMonth(costDataWindow.to)}`
                    : ""}
                  , not from any single breed. {trendSummary} What you actually
                  pay comes down to your dog, the distance, and the time of
                  year, so run the calculator above for a number that fits your
                  route.
                </p>
                {/* Widen on mobile — see the calculator wrapper above. */}
                <div className="-mx-4 md:mx-0">
                  <CostPerMileTrendChart
                    trend={marketplaceTrend}
                    ariaLabel={trendSummary}
                  />
                </div>
              </div>
            )
          })()}
      </div>
    </section>
  )
}
